import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { has, pickBy } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'qs';

import DropdownRequest from '../../../../components/Dropdown/Request';
import TableHeaderCheckboxAll from '../../../../components/Table/CheckAll';

import { generateTableHead } from '../../helpers';
import { getUsersLevels, getUsersStatuses } from '../../../../services/endpoints/users';

const TableHead = ({ handleSelect, filters, values, onChangeCheck, accessMassActions, statuses, config, columns, fields }) => {
  const history = useHistory();
  const location = useLocation();
  const queryString = qs.parse(location.search.slice(1));
  const { darkMode } = useSelector((state) => state.general);

  const [headerFilters, setHeaderFilters] = useState({
    statuses,
    levels: config?.discounts ? [{ id: null, count: null, title: 'All' }, ...config?.discounts] : [],
  });

  const availableFilterLevels = !!config?.access?.discount_counts;

  const selectedStatuses = useMemo(
    () => {
      if (statuses?.length) {
        return statuses?.filter((status) => !!filters?.statuses?.includes(status.id));
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, filters?.statuses],
  );

  const availableItemAction = () => !config.access?.copy_custom_rates
    && !config.access?.change_status
    && !config.access?.get_custom_rates
    && !config.access?.get_user
    && !config.access?.update_user;

  const sendSortValue = (e, item) => {
    e.preventDefault();

    if (item.active === 'desc') {
      delete queryString.page;
      queryString.sort = item.asc;
    }

    if (item.active === 'asc' || !item.active) {
      delete queryString.page;
      queryString.sort = item.desc;
    }

    return history.push(`${location.pathname}?${(location.search = qs.stringify(queryString))}`);
  };

  const onSelectStatuses = (options) => {
    if (options?.length === 0 && !has(queryString, 'statuses')) return;
    const newOptions = options.filter((option) => option.id !== null).map((opt) => opt.id);
    const queryObj = {
      ...queryString,
      statuses: newOptions,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: location.pathname, search: qs.stringify({ ...pickBy(queryObj) }) });
  };

  const getStatuses = async () => {
    try {
      const response = await getUsersStatuses(queryString);
      setHeaderFilters((prevState) => ({
        ...prevState,
        statuses: response?.data?.statuses?.map((status) => ({
          ...status,
          label: status.title,
        })),
      }));
    } catch (error) {
      return error;
    }
  };

  const getLevels = async () => {
    try {
      const response = await getUsersLevels(queryString);
      let allCount = 0;
      const levels = response?.data?.discounts.map((level) => {
        allCount += level.count;
        return {
          ...level,
          title: `${level.title} ${level.count || level.count === 0 ? `(${level.count})` : ''}`,
        };
      });
      setHeaderFilters((prevState) => ({
        ...prevState,
        levels: [{ id: null, count: null, title: `All (${allCount})` }, ...levels],
      }));
    } catch (error) {
      return error;
    }
  };

  return (
    <thead>
      <tr className="users__head">
        {fields
          && generateTableHead(fields, columns, config)?.map((item, index) => {
            if (item?.title === 'All') {
              return (
                <TableHeaderCheckboxAll
                  key={index}
                  handleSelect={(...props) => handleSelect(...props, 'mass')}
                  values={values}
                  transKey="Table.MassCounterUsers"
                  transValue="{{count}} users selected"
                  onChangeCheck={onChangeCheck}
                  accessMassActions={accessMassActions}
                />
              );
            }

            if (item?.title === 'Discount' && availableFilterLevels) {
              return (
                <th className="users__status" key={index}>
                  <DropdownRequest
                    title={item.title}
                    items={headerFilters.levels}
                    queryRequest="discount"
                    onRequestFunc={() => getLevels()}
                    clearData={() => true}
                    darkMode={darkMode}
                  />
                </th>
              );
            }

            if (item?.title === 'Status') {
              return (
                <th className="users__status" key={index}>
                  <DropdownRequest
                    type="select"
                    title={headerFilters.statuses?.length === selectedStatuses?.length ? 'All statuses' : `Statuses (${selectedStatuses.length})`}
                    items={headerFilters.statuses}
                    value={selectedStatuses}
                    queryRequest="statuses"
                    onRequestFunc={() => getStatuses()}
                    clearData={() => true}
                    withServicesId={false}
                    selectSearch={false}
                    onChangeSelect={onSelectStatuses}
                    useDefaultValue={false}
                    darkMode={darkMode}
                  />
                </th>
              );
            }

            if (item?.title === 'Actions') {
              if (availableItemAction()) {
                return null;
              }
              return <th key={index}> </th>;
            }

            if (item?.name) {
              if (item?.title === 'Rates' && !config?.access?.users_custom_rates) {
                return null;
              }
              if (item?.title === 'Discount' && !config?.access?.update_discount) {
                return null;
              }

              return (
                <th className={item?.title === 'ID' ? 'p-l' : ''} key={index}>
                  <a href="# " onClick={(e) => sendSortValue(e, item)}>
                    {item?.title}
                    {' '}
                    {item?.active === 'desc' ? <FontAwesomeIcon icon={faSortDown} /> : ''}
                    {item?.active === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : ''}
                    {item?.active !== 'asc' && item?.active !== 'desc' ? <FontAwesomeIcon icon={faSort} /> : ''}
                  </a>
                </th>
              );
            }

            return <th key={index}>{item?.title}</th>;
          })}
      </tr>
    </thead>
  );
};

TableHead.propTypes = {
  handleSelect: PropTypes.func,
  filters: PropTypes.object,
  values: PropTypes.array,
  onChangeCheck: PropTypes.func,
  accessMassActions: PropTypes.array,
  statuses: PropTypes.array,
  config: PropTypes.object,
  columns: PropTypes.array,
  fields: PropTypes.array,
};

export default TableHead;
